import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { Pill } from '../../components/audit/AuditVocabulary.js';
import { ApiError, describeError } from '../../lib/api.js';
import {
  createPlan,
  fetchPlans,
  fetchSchedules,
  sharePlan,
  updatePlan,
  type AuditPlan,
} from '../../lib/audit-api.js';
import { t, formatDate } from '../../lib/i18n.js';

/**
 * The Audit Plan builder (spec §11 key screen 3).
 *
 * The act this screen is really about is **sharing**. Until a plan is shared,
 * the Process/Product Owners whose work is about to be audited have not been
 * told; once it is shared, they have prepared against it. So the screen makes
 * the three things a plan must say before it can be shared — scope, criteria and
 * a start date — visible as a checklist rather than discovered as an error, and
 * says plainly that sharing happens once.
 */

const BLANK = {
  scheduleId: '',
  title: '',
  scope: '',
  criteria: '',
  plannedStartDate: '',
  plannedEndDate: '',
  location: '',
  feasibilityNotes: '',
};

export function AuditPlans() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState({ ...BLANK });
  const [editing, setEditing] = useState<string | null>(null);

  const plans = useQuery({ queryKey: ['audit-plans'], queryFn: () => fetchPlans() });
  const schedules = useQuery({ queryKey: ['audit-schedules'], queryFn: fetchSchedules });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['audit-plans'] });
  };

  const fail = (fallback: string) => (caught: unknown) =>
    setError(describeError(caught, fallback));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        title: draft.title,
        scope: draft.scope || undefined,
        criteria: draft.criteria || undefined,
        plannedStartDate: draft.plannedStartDate || null,
        plannedEndDate: draft.plannedEndDate || null,
        location: draft.location || null,
        feasibilityNotes: draft.feasibilityNotes || undefined,
      };
      if (editing) return updatePlan(editing, body);
      return createPlan({ ...body, scheduleId: draft.scheduleId || null });
    },
    onSuccess: () => {
      setError(null);
      setDraft({ ...BLANK });
      setEditing(null);
      refresh();
    },
    onError: fail(t("That plan could not be saved.")),
  });

  const share = useMutation({
    mutationFn: (id: string) => sharePlan(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail(t("That plan could not be shared.")),
  });

  const rows: AuditPlan[] = plans.data?.plans ?? [];

  /** What is still missing before this plan can be shared. */
  const missing = (plan: AuditPlan) => {
    const gaps: string[] = [];
    if (!plan.scope?.trim()) gaps.push('scope');
    if (!plan.criteria?.trim()) gaps.push('criteria');
    if (!plan.plannedStartDate) gaps.push('a start date');
    return gaps;
  };

  const startEditing = (plan: AuditPlan) => {
    setEditing(plan.id);
    setDraft({
      scheduleId: plan.scheduleId ?? '',
      title: plan.title,
      scope: plan.scope ?? '',
      criteria: plan.criteria ?? '',
      plannedStartDate: plan.plannedStartDate ?? '',
      plannedEndDate: plan.plannedEndDate ?? '',
      location: plan.location ?? '',
      feasibilityNotes: plan.feasibilityNotes ?? '',
    });
  };

  const field = (key: keyof typeof BLANK, label: string, type = 'text') => (
    <label className="flex flex-col text-sm">
      <span className="mb-1 text-content-secondary">{label}</span>
      <input
        type={type}
        className="rounded border border-border px-2 py-1"
        value={draft[key]}
        onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
      />
    </label>
  );

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Audit plans']} />

      <main className="mx-auto max-w-5xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Audit plans")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Scope, criteria, date and location — shared with the Process and Product Owners before the audit begins.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        <form
          className="mt-6 rounded-md border border-border bg-surface p-5"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <h2 className="m-0 text-base font-semibold">{editing ? t("Edit plan") : t("New plan")}</h2>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {!editing && (
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-content-secondary">{t("Annual programme")}</span>
                <select
                  className="rounded border border-border px-2 py-1"
                  value={draft.scheduleId}
                  onChange={(event) => setDraft({ ...draft, scheduleId: event.target.value })}
                >
                  <option value="">{t("Not part of a programme")}</option>
                  {(schedules.data?.schedules ?? []).map((schedule) => (
                    <option key={schedule.id} value={schedule.id}>
                      {schedule.year} — {schedule.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {field('title', 'Title')}
            {field('plannedStartDate', 'Planned start', 'date')}
            {field('plannedEndDate', 'Planned end', 'date')}
            {field('location', 'Location')}
          </div>

          <div className="mt-3 grid gap-3">
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Scope — what is being audited")}</span>
              <textarea
                className="rounded border border-border px-2 py-1"
                rows={2}
                value={draft.scope}
                onChange={(event) => setDraft({ ...draft, scope: event.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">
                {t("Criteria — what it is being judged against")}
              </span>
              <textarea
                className="rounded border border-border px-2 py-1"
                rows={2}
                value={draft.criteria}
                onChange={(event) => setDraft({ ...draft, criteria: event.target.value })}
              />
            </label>
            <label className="flex flex-col text-sm">
              <span className="mb-1 text-content-secondary">{t("Feasibility notes")}</span>
              <textarea
                className="rounded border border-border px-2 py-1"
                rows={2}
                value={draft.feasibilityNotes}
                onChange={(event) => setDraft({ ...draft, feasibilityNotes: event.target.value })}
              />
            </label>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="submit"
              className="rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
              disabled={save.isPending || !draft.title.trim()}
            >
              {editing ? t("Save changes") : t("Create plan")}
            </button>
            {editing && (
              <button
                type="button"
                className="rounded border border-border px-4 py-2 text-sm"
                onClick={() => {
                  setEditing(null);
                  setDraft({ ...BLANK });
                }}
              >
                {t("Cancel")}
              </button>
            )}
          </div>
        </form>

        {plans.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {!plans.isLoading && rows.length === 0 && (
          <p className="mt-6 rounded-md border border-border bg-surface p-6 text-content-secondary">
            {t("No audit plans yet.")}
          </p>
        )}

        <ul className="mt-6 list-none space-y-4 p-0">
          {rows.map((plan) => {
            const gaps = missing(plan);
            return (
              <li
                key={plan.id}
                className="rounded-md border border-border bg-surface p-5 shadow-card"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="m-0 text-base font-semibold">{plan.title}</h2>
                  {plan.sharedAt ? (
                    <Pill tone="positive">
                      {t("Shared")}{' '}{formatDate(plan.sharedAt)}
                    </Pill>
                  ) : (
                    <Pill>{t("Not shared")}</Pill>
                  )}
                </div>

                <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="inline text-content-secondary">{t("Dates:")}{' '}</dt>
                    <dd className="inline">
                      {plan.plannedStartDate ?? '—'}
                      {plan.plannedEndDate ? t(" to {0}", { 0: plan.plannedEndDate }) : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline text-content-secondary">{t("Location:")}{' '}</dt>
                    <dd className="inline">{plan.location ?? '—'}</dd>
                  </div>
                </dl>

                {plan.scope && <p className="mt-2 text-sm">{plan.scope}</p>}

                {!plan.sharedAt && gaps.length > 0 && (
                  <p className="mt-3 rounded border border-border bg-tone-info-surface p-3 text-sm">
                    {t("Before this can be shared it still needs")}{' '}{gaps.join(', ')}{t(". An owner cannot prepare against blanks.")}
                  </p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {!plan.sharedAt && (
                    <>
                      <button
                        type="button"
                        className="rounded border border-border px-3 py-1.5 text-sm"
                        onClick={() => startEditing(plan)}
                      >
                        {t("Edit")}
                      </button>
                      <button
                        type="button"
                        className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white"
                        onClick={() => share.mutate(plan.id)}
                        disabled={gaps.length > 0 || share.isPending}
                      >
                        {t("Share with process owners")}
                      </button>
                    </>
                  )}
                  {plan.sharedAt && (
                    <p className="m-0 text-sm text-content-secondary">
                      {t("Shared once, deliberately. Re-issuing a changed plan is a new act, so that the date people were actually told is not overwritten.")}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}
